/**
 * WorkingTimer — a live "still working" indicator for long runs (generating images, rendering
 * mock-ups): a small CSS orbit in the product's progress accent plus the elapsed time since
 * `startedAt`. `done` / `failed` stop the clock — at `endedAt` when the caller knows it, else
 * at the last tick — and drop the motion.
 *
 * `role="timer"` is not a live region, so a screen reader reads the time when asked instead of
 * being interrupted every second.
 */
import { useEffect, useState } from "react";
import "./ui.css";

export type WorkingTimerState = "working" | "done" | "failed";

export interface WorkingTimerProps {
  /** When the work started: an ISO string or epoch milliseconds. */
  startedAt: string | number;
  /** What is running, e.g. "Rendering". */
  label?: string;
  state?: WorkingTimerState;
  /** When the work ended, if known; used once the state is `done` or `failed`. */
  endedAt?: string | number;
}

const toMs = (t: string | number) => (typeof t === "number" ? t : Date.parse(t));
const pad2 = (n: number) => String(n).padStart(2, "0");

/** Elapsed time as m:ss, or h:mm:ss from an hour on. Negative reads as 0:00. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
}

function useNow(ticking: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [ticking]);
  return now;
}

export function WorkingTimer({ startedAt, label, state = "working", endedAt }: WorkingTimerProps) {
  const working = state === "working";
  const now = useNow(working);
  const end = !working && endedAt !== undefined ? toMs(endedAt) : now;
  const elapsed = end - toMs(startedAt);
  return (
    <span className="working-timer" role="timer" data-state={state} data-testid="working-timer">
      {working && (
        <span className="working-timer__orbit" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      )}
      {label && <span className="working-timer__label">{label}</span>}
      <time className="working-timer__time" dateTime={`PT${Math.max(0, Math.floor(elapsed / 1000))}S`}>
        {formatElapsed(elapsed)}
      </time>
    </span>
  );
}
